import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Chip, Alert, Snackbar, Tooltip,
  Accordion, AccordionSummary, AccordionDetails, Grid, Badge, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Security as SecurityIcon,
  ExpandMore as ExpandMoreIcon, Key as KeyIcon, Visibility as VisibilityIcon, Lock as LockIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import PermissionService from '../../services/permission.service';

const PermissionManagement = () => {
  const { hasPermission } = useAuth();
  const { t } = useTranslation();
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingPermission, setEditingPermission] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filterModule, setFilterModule] = useState('all');

  const [formData, setFormData] = useState({
    name: ''
  });

  const deriveGroupFromName = (permissionName = '') => {
    if (!permissionName.includes('.')) return '';
    const [module] = permissionName.split('.');
    return module ? module.charAt(0).toUpperCase() + module.slice(1) : '';
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await PermissionService.getAll();
      setPermissions(response.data);
    } catch (error) {
      console.error('Error loading permissions:', error);
      showSnackbar(t('permissions.errorLoadingPermissions'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleOpenDialog = (permission = null) => {
    if (permission) {
      setEditingPermission(permission);
      setFormData({
        name: permission.name
      });
    } else {
      setEditingPermission(null);
      setFormData({
        name: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPermission(null);
    setFormData({
      name: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPermission) {
        await PermissionService.update(editingPermission.id, formData);
        showSnackbar(t('permissions.permissionUpdatedSuccess'));
      } else {
        await PermissionService.create(formData);
        showSnackbar(t('permissions.permissionCreatedSuccess'));
      }

      handleCloseDialog();
      loadData();
    } catch (error) {
      console.error('Error saving permission:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          (error.response?.data?.errors ? 
                            Object.values(error.response.data.errors).flat().join(', ') : 
                            'Error saving permission');
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async (permissionId) => {
    if (window.confirm('Are you sure you want to delete this permission?')) {
      try {
        await PermissionService.delete(permissionId);
        showSnackbar(t('permissions.permissionDeletedSuccess'));
        loadData();
      } catch (error) {
        console.error('Error deleting permission:', error);
        showSnackbar(t('permissions.errorDeletingPermission'), 'error');
      }
    }
  };

  const groupedPermissions = permissions.reduce((acc, permission) => {
    const [module] = permission.name.split('.');
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(permission);
    return acc;
  }, {});

  const filteredPermissions = filterModule === 'all'
    ? permissions
    : permissions.filter(p => p.name.startsWith(filterModule + '.'));

  const filteredGroupedPermissions = filterModule === 'all'
    ? groupedPermissions
    : { [filterModule]: groupedPermissions[filterModule] || [] };

  const modules = Object.keys(groupedPermissions).sort();

  const getPermissionIcon = (permissionName) => {
    if (permissionName.includes('view')) return <VisibilityIcon />;
    if (permissionName.includes('create') || permissionName.includes('add')) return <AddIcon />;
    if (permissionName.includes('edit') || permissionName.includes('update')) return <EditIcon />;
    if (permissionName.includes('delete')) return <DeleteIcon />;
    return <KeyIcon />;
  };

  const getPermissionColor = (permissionName) => {
    if (permissionName.includes('view')) return 'info';
    if (permissionName.includes('create') || permissionName.includes('add')) return 'success';
    if (permissionName.includes('edit') || permissionName.includes('update')) return 'warning';
    if (permissionName.includes('delete')) return 'error';
    return 'default';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading permissions...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h1">
              Permission Management
            </Typography>
            <Box display="flex" gap={2}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Filter by Module</InputLabel>
                <Select
                  value={filterModule}
                  onChange={(e) => setFilterModule(e.target.value)}
                  label="Filter by Module"
                >
                  <MenuItem value="all">All Modules</MenuItem>
                  {modules.map((module) => (
                    <MenuItem key={module} value={module}>
                      {module.charAt(0).toUpperCase() + module.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {hasPermission('permissions.create') && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog()}
                >
                  Add Permission
                </Button>
              )}
            </Box>
          </Box>

          {/* Grouped View */}
          <Box mb={3}>
            {Object.entries(filteredGroupedPermissions).map(([module, modulePermissions]) => (
              <Accordion key={module} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <SecurityIcon color="primary" />
                    <Typography variant="h6">
                      {module.charAt(0).toUpperCase() + module.slice(1)}
                    </Typography>
                    <Badge badgeContent={modulePermissions.length} color="primary" />
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={1}>
                    {modulePermissions.map((permission) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={permission.id}>
                        <Card variant="outlined" sx={{ p: 1 }}>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="center" gap={1}>
                              {getPermissionIcon(permission.name)}
                              <Typography variant="body2" fontWeight="medium">
                                {permission.name}
                              </Typography>
                            </Box>
                            <Box display="flex" gap={0.5}>
                              {hasPermission('permissions.edit') && (
                                <Tooltip title="Edit permission">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(permission)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {hasPermission('permissions.delete') && (
                                <Tooltip title="Delete permission">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(permission.id)}
                                    color="error"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          </Box>
                          <Box mt={1}>
                            {deriveGroupFromName(permission.name) && (
                              <Chip
                                label={deriveGroupFromName(permission.name)}
                                size="small"
                                color="secondary"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>

          {/* Table View */}
          <Typography variant="h6" gutterBottom>
            All Permissions ({filteredPermissions.length})
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Permission</TableCell>
                  <TableCell>Module</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Guard</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPermissions.map((permission) => {
                  const [module, action] = permission.name.split('.');
                  return (
                    <TableRow key={permission.id}>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getPermissionIcon(permission.name)}
                          <Typography variant="body2" fontWeight="medium">
                            {permission.name}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={module}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={action}
                          size="small"
                          color={getPermissionColor(permission.name)}
                          variant="filled"
                        />
                      </TableCell>
                      <TableCell>
                        {deriveGroupFromName(permission.name) && (
                          <Chip
                            label={deriveGroupFromName(permission.name)}
                            size="small"
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(permission.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" gap={1}>
                          {hasPermission('permissions.edit') && (
                            <Tooltip title="Edit permission">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog(permission)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasPermission('permissions.delete') && (
                            <Tooltip title="Delete permission">
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(permission.id)}
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Permission Form Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={1}>
              <KeyIcon color="primary" />
              {editingPermission ? 'Edit Permission' : 'Create Permission'}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} pt={1}>
              <TextField
                label="Permission Name"
                value={formData.name}
                onChange={(e) => {
                  setFormData((prev) => ({
                    ...prev,
                    name: e.target.value
                  }));
                }}
                required
                fullWidth
                placeholder="e.g., users.view, clients.create"
                helperText="Format: module.action (e.g., users.view, clients.create)"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPermission ? 'Update Permission' : 'Create Permission'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PermissionManagement;
